// MACHINERY V2 (Stroje) — the shared Radix dialog shell for the tab dialogs
// (M2.5).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.5. Reference impl:
// design_handoff_machinery_v3 `MDialog` (glyph + title + body + footer note +
// footer buttons); artboards 1004 (docs upload), 1005 (docs rename) and 1011
// (parts add-from-stock).
//
// WHY THESE FOUR DIALOGS ARE REBUILT AND NOT REUSED. Ticket §5 says v2 REUSES
// the existing MUI dialogs for the kebab actions, and FleetDialogs/ProfileDialogs
// remain the only sanctioned MUI mounts. The M2.5 dialogs are the exception, and
// each has a specific reason:
//   · Docs RENAME — ticket §7 FIX 4 lives inside it. The wrong "100" copy is
//     produced by components/manualsAndSoftware/DocumentFileItem.tsx:82, which
//     passes `translate("titleTooLong")` next to a `maxLength: { value: 50 }`.
//     Reusing the MUI dialog would mean editing a live v1 component to fix it.
//   · Docs UPLOAD — artboard 1004 rejects only the oversize FILE; v1's
//     UploadDocument.tsx:79-83 calls `setValue("documents", [])`, wiping every
//     already-queued file. Different behaviour, and it renders a different list.
//   · Parts ADD — artboard 1011 is a quantity STEPPER row list; v1's
//     InternalStockDialog is a 410-line MUI table with its own pagination.
//   · SR SHARE — 65 lines of "read-only URL + copy" (ServiceReportShareDialog),
//     and reusing it would mean threading share state through ProfileDialogs to
//     mount ONE MUI import for a text field.
// None of these adds an MUI mount: they are Radix, which is the sanctioned v2
// primitive library (§3).
//
// PORTAL RULE (MachineryV2Layout.tsx:20-39, ReadinessPopupV2.tsx:17-27):
// Dialog.Content portals to document.body, outside `.machinery-v2-root`, where
// the scoped vars do not resolve. Content re-carries
// `machinery-v2-root p-<palette> mod-stroje`, sets inline
// background:"transparent" (the root's opaque var(--bg) would otherwise slab the
// screen behind the overlay), paints the real surface on the inner div, and
// reads `palette` LIVE from the slice on every render rather than capturing it
// at mount. The Overlay deliberately does NOT carry the scope class — it is a
// fixed rgba scrim and classing it would repaint var(--bg).
//
// STOP PROPAGATION ON BOTH PORTALLED SIBLINGS. Radix splits the portal into
// Overlay AND Content; React synthetic events bubble the COMPONENT tree back
// into whatever rendered this dialog (a table row, a card), so guarding only one
// leaks backdrop clicks into the opener. Both are guarded here, once, for all
// four dialogs.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import * as Dialog from "@radix-ui/react-dialog";

import { useAppSelector } from "../../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../../shared/hooks/useTranslation";

export interface TabDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Small square teal glyph left of the title (artboards 1004 / 1011). */
  glyph?: React.ReactNode;
  width?: number;
  /** Mono note on the footer-left explaining what the primary action does. */
  footerNote?: string;
  footer?: React.ReactNode;
  children: React.ReactNode;
}

const TabDialog: React.FC<TabDialogProps> = ({
  open,
  onOpenChange,
  title,
  glyph,
  width = 600,
  footerNote,
  footer,
  children,
}) => {
  const { translate } = useTranslation("myMachinery");
  const palette = useAppSelector((s) => s.machineryV2Ui.palette);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(12, 17, 23, 0.35)",
            zIndex: 1300,
          }}
        />
        <Dialog.Content
          className={`machinery-v2-root p-${palette} mod-stroje`}
          aria-describedby={undefined}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1301,
            display: "flex",
            width,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "calc(100vh - 32px)",
            background: "transparent",
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              background: "var(--card)",
              borderRadius: 12,
              border: "1px solid var(--border)",
              boxShadow:
                "0 24px 64px rgba(15,23,30,0.22), 0 4px 16px rgba(15,23,30,0.10)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 11,
                padding: "15px 18px 12px",
                minWidth: 0,
              }}
            >
              {glyph && (
                <span
                  style={{
                    display: "inline-grid",
                    placeItems: "center",
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    background: "var(--mod-accent-soft)",
                    border: "1px solid var(--mod-accent-line)",
                    color: "var(--mod-accent)",
                    flexShrink: 0,
                  }}
                >
                  {glyph}
                </span>
              )}
              <Dialog.Title asChild>
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 16,
                    fontWeight: 600,
                    color: "var(--ink)",
                    letterSpacing: "-0.01em",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {title}
                </div>
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="btn btn-ghost"
                  aria-label={translate("myMachinery:close")}
                  style={{ width: 30, padding: 0, fontSize: 15, justifyContent: "center" }}
                >
                  &times;
                </button>
              </Dialog.Close>
            </div>

            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                padding: "0 18px 16px",
                minWidth: 0,
              }}
            >
              {children}
            </div>

            {(footer || footerNote) && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "11px 18px",
                  borderTop: "1px solid var(--border-soft)",
                  background: "var(--bg-soft)",
                  flexWrap: "wrap",
                }}
              >
                {footerNote && (
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 9.5,
                      color: "var(--faint)",
                      letterSpacing: "0.04em",
                      minWidth: 0,
                    }}
                  >
                    {footerNote}
                  </span>
                )}
                <span style={{ flex: 1 }} />
                {footer}
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default TabDialog;
